// Demo Data for OutboundBuddy Platform
// Impressive, "wow factor" data that showcases platform capabilities
//
// DEMO SYSTEM DESIGN OVERVIEW
// - Domain Strategy: Each client has a primary domain (established brand) and, for
//   medium/large clients, a secondary domain (fresh sender pool for experiments or
//   overflow). This mirrors common outbound best practices to protect domain health.
// - Mailbox Distribution: Mailbox counts scale by company size. Large enterprises
//   operate 20–25 mailboxes, medium 16–24, small/SMB 12–15. Total is exactly 156.
// - Sending Volumes: Dashboard target is ~2,847 emails/day which equals ~18 emails
//   per mailbox on average (2,847 / 156 ≈ 18.25). We distribute per-mailbox limits
//   so that some mailboxes send 15–20/day (active), some 10–15/day (moderate), some
//   5–10/day (warming), and a few 0–5/day (new/paused) to look realistic.
// - Warmup & Reputation: Primary domains have completed/active warmup and higher
//   reputation (85–95). Secondary domains are newer with active/warming status and
//   slightly lower reputation (75–85). This produces believable health metrics and
//   capacity numbers for demos.
// - Data Consistency: The constants and utility functions below allow other hooks to
//   derive consistent, reproducible demo metrics for domains, mailboxes and clients.

export interface DemoClient {
  id: string
  name: string
  industry: string
  company_size: string
  status: 'Active' | 'Paused' | 'Onboarding'
  created_at: string
  campaigns_count: number
  total_leads: number
  reply_rate: number
  positive_reply_rate: number
  client_id: string
  contact_name: string
  contact_email: string
  contact_phone?: string
}

export interface DemoCampaign {
  id: string
  name: string
  client_id: string
  client_name: string
  status: 'Active' | 'Paused' | 'Completed' | 'Draft'
  campaign_type: 'bulk' | 'adhoc' | 'events' | 'triggers'
  created_at: string
  last_activity: string
  
  // Performance Metrics
  total_leads: number
  emails_sent: number
  emails_opened: number
  emails_clicked: number
  replies_received: number
  positive_replies: number
  meetings_booked: number
  deals_closed: number
  
  // Rates (impressive percentages)
  open_rate: number
  click_rate: number
  reply_rate: number
  positive_reply_rate: number
  meeting_rate: number
  close_rate: number
  
  // Lead Categories
  hot_leads: number
  warm_leads: number
  cold_leads: number
  interested_leads: number
  not_interested_leads: number
  
  // Variants
  total_variants: number
  active_variants: number
  best_performing_variant: string
}

export interface DemoCampaignAnalytics {
  campaign_id: string
  date: string
  emails_sent: number
  emails_opened: number
  emails_clicked: number
  replies_received: number
  positive_replies: number
  meetings_booked: number
  bounces: number
  unsubscribes: number
}

export interface DemoLead {
  id: string
  email: string
  first_name: string
  last_name: string
  company: string
  title: string
  industry: string
  campaign_id: string
  campaign_name: string
  client_id: string
  status: 'sent' | 'opened' | 'clicked' | 'replied' | 'positive_reply' | 'meeting_booked' | 'deal_won'
  lead_score: 'Hot' | 'Warm' | 'Cold' | 'Interested' | 'Not Interested'
  created_at: string
  last_activity: string
  emails_sent: number
  reply_body?: string
  meeting_date?: string
  deal_value?: number
}

export interface DemoDomain {
  id: string
  domain: string
  client_id: string
  type: 'primary' | 'secondary'
  registrar: string
  status: 'Active' | 'Expired' | 'Pending'
  purchase_date: string
  renewal_date: string
  expiry_date: string
  auto_renewal: boolean
  notes?: string
  // Metadata for infrastructure UI (optional, used for richer demo rendering)
  // Lowercase fields mirror requested API for future compatibility
  status_meta?: 'active' | 'warming' | 'paused'
  spfConfigured?: boolean
  dkimConfigured?: boolean
  dmarcConfigured?: boolean
  customTrackingDomain?: boolean
  dnsHealth?: 'healthy' | 'warning' | 'error'
  registrationDate?: string
  mailboxCount?: number
}

export interface DemoMailbox {
  id: string
  email: string
  domain: string
  from_name: string
  client_id: string
  status: 'Active' | 'Paused' | 'Warming Up'
  warmup_status: 'ACTIVE' | 'INACTIVE' | 'DISABLED'
  warmup_reputation: number
  daily_limit: number
  emails_sent_today: number
  reply_rate: number
  bounce_rate: number
  created_at: string
  last_activity: string
  provider: 'GMAIL' | 'OUTLOOK' | 'OTHER'
  type: 'primary' | 'secondary'
}

export interface DemoVariant {
  id: string
  campaign_id: string
  name: string
  subject_line: string
  email_template: string
  status: 'Active' | 'Paused' | 'Completed'
  emails_sent: number
  open_rate: number
  click_rate: number
  reply_rate: number
  positive_reply_rate: number
  is_winner: boolean
  created_at: string
}

// ============================================================================
// DEMO CLIENTS (6-8 impressive companies)
// ============================================================================

export const DEMO_CLIENTS: DemoClient[] = [
  {
    id: '5559',
    name: 'NorthStar Analytics',
    industry: 'Data Analytics & AI',
    company_size: '50-200 employees',
    status: 'Active',
    created_at: '2024-08-15T10:00:00Z',
    campaigns_count: 4,
    total_leads: 1247,
    reply_rate: 18.4,
    positive_reply_rate: 11.2,
    client_id: 'NS-2024-001',
    contact_name: 'Sarah Chen',
    contact_email: 'sarah.chen@northstaranalytics.com',
    contact_phone: '+1 (555) 123-4567'
  },
  {
    id: '8241',
    name: 'Velocity Marketing Group',
    industry: 'Marketing & Growth',
    company_size: '20-100 employees',
    status: 'Active',
    created_at: '2024-09-02T14:30:00Z',
    campaigns_count: 3,
    total_leads: 892,
    reply_rate: 16.8,
    positive_reply_rate: 9.7
  },
  {
    id: '7392',
    name: 'Summit Tech Solutions',
    industry: 'Enterprise Software',
    company_size: '200-500 employees',
    status: 'Active',
    created_at: '2024-07-20T09:15:00Z',
    campaigns_count: 5,
    total_leads: 2156,
    reply_rate: 21.3,
    positive_reply_rate: 13.1
  },
  {
    id: '4567',
    name: 'Innovate Design Studio',
    industry: 'Creative & Design',
    company_size: '10-50 employees',
    status: 'Active',
    created_at: '2024-10-01T11:45:00Z',
    campaigns_count: 2,
    total_leads: 634,
    reply_rate: 19.7,
    positive_reply_rate: 12.4
  },
  {
    id: '2891',
    name: 'Acme Corporation',
    industry: 'Manufacturing & Industrial',
    company_size: '500+ employees',
    status: 'Active',
    created_at: '2024-06-10T16:20:00Z',
    campaigns_count: 6,
    total_leads: 3421,
    reply_rate: 15.2,
    positive_reply_rate: 8.9
  },
  {
    id: '6734',
    name: 'TechVentures Capital',
    industry: 'Venture Capital',
    company_size: '20-100 employees',
    status: 'Active',
    created_at: '2024-09-15T13:10:00Z',
    campaigns_count: 3,
    total_leads: 478,
    reply_rate: 22.1,
    positive_reply_rate: 14.6
  },
  {
    id: '8923',
    name: 'Global Logistics Pro',
    industry: 'Logistics & Supply Chain',
    company_size: '100-500 employees',
    status: 'Active',
    created_at: '2024-08-28T08:30:00Z',
    campaigns_count: 4,
    total_leads: 1156,
    reply_rate: 17.9,
    positive_reply_rate: 10.8
  },
  {
    id: '3456',
    name: 'HealthTech Innovations',
    industry: 'Healthcare Technology',
    company_size: '50-200 employees',
    status: 'Active',
    created_at: '2024-07-05T12:00:00Z',
    campaigns_count: 3,
    total_leads: 789,
    reply_rate: 20.5,
    positive_reply_rate: 12.7
  }
]

// ============================================================================
// DEMO CAMPAIGNS (15 impressive campaigns)
// ============================================================================

export const DEMO_CAMPAIGNS: DemoCampaign[] = [
  {
    id: '1',
    name: 'Q4 Enterprise Outreach - Fortune 500',
    client_id: '5559',
    client_name: 'NorthStar Analytics',
    status: 'Active',
    campaign_type: 'bulk',
    created_at: '2024-10-01T09:00:00Z',
    last_activity: '2024-10-17T14:30:00Z',
    
    total_leads: 487,
    emails_sent: 487,
    emails_opened: 341,
    emails_clicked: 89,
    replies_received: 89,
    positive_replies: 54,
    meetings_booked: 23,
    deals_closed: 7,
    
    open_rate: 70.0,
    click_rate: 18.3,
    reply_rate: 18.3,
    positive_reply_rate: 11.1,
    meeting_rate: 4.7,
    close_rate: 1.4,
    
    hot_leads: 23,
    warm_leads: 67,
    cold_leads: 234,
    interested_leads: 89,
    not_interested_leads: 74,
    
    total_variants: 4,
    active_variants: 3,
    best_performing_variant: 'Personalized AI Insights'
  },
  {
    id: '2',
    name: 'Product Launch - Series B Targets',
    client_id: '8241',
    client_name: 'Velocity Marketing Group',
    status: 'Active',
    campaign_type: 'events',
    created_at: '2024-09-15T11:30:00Z',
    last_activity: '2024-10-17T16:45:00Z',
    
    total_leads: 234,
    emails_sent: 234,
    emails_opened: 175,
    emails_clicked: 52,
    replies_received: 47,
    positive_replies: 28,
    meetings_booked: 12,
    deals_closed: 4,
    
    open_rate: 74.8,
    click_rate: 22.2,
    reply_rate: 20.1,
    positive_reply_rate: 12.0,
    meeting_rate: 5.1,
    close_rate: 1.7,
    
    hot_leads: 12,
    warm_leads: 28,
    cold_leads: 98,
    interested_leads: 47,
    not_interested_leads: 49,
    
    total_variants: 3,
    active_variants: 2,
    best_performing_variant: 'Exclusive Early Access'
  },
  {
    id: '3',
    name: 'Partnership Development - Strategic Alliances',
    client_id: '7392',
    client_name: 'Summit Tech Solutions',
    status: 'Active',
    campaign_type: 'adhoc',
    created_at: '2024-08-20T14:15:00Z',
    last_activity: '2024-10-17T10:20:00Z',
    
    total_leads: 156,
    emails_sent: 156,
    emails_opened: 124,
    emails_clicked: 38,
    replies_received: 33,
    positive_replies: 21,
    meetings_booked: 8,
    deals_closed: 3,
    
    open_rate: 79.5,
    click_rate: 24.4,
    reply_rate: 21.2,
    positive_reply_rate: 13.5,
    meeting_rate: 5.1,
    close_rate: 1.9,
    
    hot_leads: 8,
    warm_leads: 21,
    cold_leads: 67,
    interested_leads: 33,
    not_interested_leads: 27,
    
    total_variants: 2,
    active_variants: 2,
    best_performing_variant: 'Mutual Growth Opportunity'
  },
  {
    id: '4',
    name: 'Investor Relations - Growth Story',
    client_id: '6734',
    client_name: 'TechVentures Capital',
    status: 'Active',
    campaign_type: 'bulk',
    created_at: '2024-09-01T10:45:00Z',
    last_activity: '2024-10-17T15:10:00Z',
    
    total_leads: 89,
    emails_sent: 89,
    emails_opened: 71,
    emails_clicked: 24,
    replies_received: 20,
    positive_replies: 13,
    meetings_booked: 6,
    deals_closed: 2,
    
    open_rate: 79.8,
    click_rate: 27.0,
    reply_rate: 22.5,
    positive_reply_rate: 14.6,
    meeting_rate: 6.7,
    close_rate: 2.2,
    
    hot_leads: 6,
    warm_leads: 13,
    cold_leads: 34,
    interested_leads: 20,
    not_interested_leads: 16,
    
    total_variants: 3,
    active_variants: 3,
    best_performing_variant: 'Portfolio Company Success'
  },
  {
    id: '5',
    name: 'Enterprise Security Solutions',
    client_id: '7392',
    client_name: 'Summit Tech Solutions',
    status: 'Active',
    campaign_type: 'bulk',
    created_at: '2024-07-25T13:20:00Z',
    last_activity: '2024-10-17T12:30:00Z',
    
    total_leads: 678,
    emails_sent: 678,
    emails_opened: 475,
    emails_clicked: 142,
    replies_received: 135,
    positive_replies: 89,
    meetings_booked: 34,
    deals_closed: 12,
    
    open_rate: 70.1,
    click_rate: 20.9,
    reply_rate: 19.9,
    positive_reply_rate: 13.1,
    meeting_rate: 5.0,
    close_rate: 1.8,
    
    hot_leads: 34,
    warm_leads: 89,
    cold_leads: 298,
    interested_leads: 135,
    not_interested_leads: 122,
    
    total_variants: 5,
    active_variants: 4,
    best_performing_variant: 'Zero-Trust Architecture'
  },
  {
    id: '6',
    name: 'Creative Agency Partnership Outreach',
    client_id: '4567',
    client_name: 'Innovate Design Studio',
    status: 'Active',
    campaign_type: 'adhoc',
    created_at: '2024-10-05T16:00:00Z',
    last_activity: '2024-10-17T11:15:00Z',
    
    total_leads: 123,
    emails_sent: 123,
    emails_opened: 98,
    emails_clicked: 31,
    replies_received: 24,
    positive_replies: 15,
    meetings_booked: 7,
    deals_closed: 2,
    
    open_rate: 79.7,
    click_rate: 25.2,
    reply_rate: 19.5,
    positive_reply_rate: 12.2,
    meeting_rate: 5.7,
    close_rate: 1.6,
    
    hot_leads: 7,
    warm_leads: 15,
    cold_leads: 52,
    interested_leads: 24,
    not_interested_leads: 25,
    
    total_variants: 2,
    active_variants: 2,
    best_performing_variant: 'Brand Transformation Case Study'
  },
  {
    id: '7',
    name: 'Manufacturing Automation Solutions',
    client_id: '2891',
    client_name: 'Acme Corporation',
    status: 'Active',
    campaign_type: 'bulk',
    created_at: '2024-06-15T08:30:00Z',
    last_activity: '2024-10-17T09:45:00Z',
    
    total_leads: 892,
    emails_sent: 892,
    emails_opened: 625,
    emails_clicked: 187,
    replies_received: 178,
    positive_replies: 112,
    meetings_booked: 45,
    deals_closed: 18,
    
    open_rate: 70.1,
    click_rate: 21.0,
    reply_rate: 20.0,
    positive_reply_rate: 12.6,
    meeting_rate: 5.0,
    close_rate: 2.0,
    
    hot_leads: 45,
    warm_leads: 112,
    cold_leads: 392,
    interested_leads: 178,
    not_interested_leads: 165,
    
    total_variants: 6,
    active_variants: 5,
    best_performing_variant: 'ROI Calculator - 30% Efficiency Gain'
  },
  {
    id: '8',
    name: 'Healthcare AI Implementation',
    client_id: '3456',
    client_name: 'HealthTech Innovations',
    status: 'Active',
    campaign_type: 'events',
    created_at: '2024-07-10T12:15:00Z',
    last_activity: '2024-10-17T14:00:00Z',
    
    total_leads: 267,
    emails_sent: 267,
    emails_opened: 201,
    emails_clicked: 67,
    replies_received: 55,
    positive_replies: 34,
    meetings_booked: 16,
    deals_closed: 6,
    
    open_rate: 75.3,
    click_rate: 25.1,
    reply_rate: 20.6,
    positive_reply_rate: 12.7,
    meeting_rate: 6.0,
    close_rate: 2.2,
    
    hot_leads: 16,
    warm_leads: 34,
    cold_leads: 112,
    interested_leads: 55,
    not_interested_leads: 50,
    
    total_variants: 4,
    active_variants: 3,
    best_performing_variant: 'Patient Outcome Improvement'
  },
  {
    id: '9',
    name: 'Logistics Optimization Platform',
    client_id: '8923',
    client_name: 'Global Logistics Pro',
    status: 'Active',
    campaign_type: 'bulk',
    created_at: '2024-08-30T15:45:00Z',
    last_activity: '2024-10-17T13:20:00Z',
    
    total_leads: 445,
    emails_sent: 445,
    emails_opened: 334,
    emails_clicked: 100,
    replies_received: 89,
    positive_replies: 56,
    meetings_booked: 22,
    deals_closed: 8,
    
    open_rate: 75.1,
    click_rate: 22.5,
    reply_rate: 20.0,
    positive_reply_rate: 12.6,
    meeting_rate: 4.9,
    close_rate: 1.8,
    
    hot_leads: 22,
    warm_leads: 56,
    cold_leads: 196,
    interested_leads: 89,
    not_interested_leads: 82,
    
    total_variants: 4,
    active_variants: 3,
    best_performing_variant: 'Cost Reduction Guarantee'
  },
  {
    id: '10',
    name: 'Data Analytics Platform Launch',
    client_id: '5559',
    client_name: 'NorthStar Analytics',
    status: 'Completed',
    campaign_type: 'events',
    created_at: '2024-08-01T10:00:00Z',
    last_activity: '2024-10-10T16:30:00Z',
    
    total_leads: 334,
    emails_sent: 334,
    emails_opened: 267,
    emails_clicked: 80,
    replies_received: 67,
    positive_replies: 41,
    meetings_booked: 18,
    deals_closed: 7,
    
    open_rate: 79.9,
    click_rate: 24.0,
    reply_rate: 20.1,
    positive_reply_rate: 12.3,
    meeting_rate: 5.4,
    close_rate: 2.1,
    
    hot_leads: 18,
    warm_leads: 41,
    cold_leads: 147,
    interested_leads: 67,
    not_interested_leads: 61,
    
    total_variants: 3,
    active_variants: 0,
    best_performing_variant: 'Free Analytics Audit'
  },
  {
    id: '11',
    name: 'Marketing Automation for SMBs',
    client_id: '8241',
    client_name: 'Velocity Marketing Group',
    status: 'Active',
    campaign_type: 'triggers',
    created_at: '2024-09-20T11:00:00Z',
    last_activity: '2024-10-17T17:30:00Z',
    
    total_leads: 189,
    emails_sent: 189,
    emails_opened: 142,
    emails_clicked: 43,
    replies_received: 38,
    positive_replies: 23,
    meetings_booked: 11,
    deals_closed: 4,
    
    open_rate: 75.1,
    click_rate: 22.8,
    reply_rate: 20.1,
    positive_reply_rate: 12.2,
    meeting_rate: 5.8,
    close_rate: 2.1,
    
    hot_leads: 11,
    warm_leads: 23,
    cold_leads: 83,
    interested_leads: 38,
    not_interested_leads: 34,
    
    total_variants: 2,
    active_variants: 2,
    best_performing_variant: '30-Day Free Trial'
  },
  {
    id: '12',
    name: 'Enterprise Cloud Migration',
    client_id: '7392',
    client_name: 'Summit Tech Solutions',
    status: 'Paused',
    campaign_type: 'bulk',
    created_at: '2024-07-30T14:30:00Z',
    last_activity: '2024-10-15T10:00:00Z',
    
    total_leads: 523,
    emails_sent: 523,
    emails_opened: 366,
    emails_clicked: 110,
    replies_received: 94,
    positive_replies: 58,
    meetings_booked: 26,
    deals_closed: 9,
    
    open_rate: 70.0,
    click_rate: 21.0,
    reply_rate: 18.0,
    positive_reply_rate: 11.1,
    meeting_rate: 5.0,
    close_rate: 1.7,
    
    hot_leads: 26,
    warm_leads: 58,
    cold_leads: 230,
    interested_leads: 94,
    not_interested_leads: 115,
    
    total_variants: 5,
    active_variants: 0,
    best_performing_variant: 'Zero-Downtime Migration'
  },
  {
    id: '13',
    name: 'Creative Portfolio Showcase',
    client_id: '4567',
    client_name: 'Innovate Design Studio',
    status: 'Active',
    campaign_type: 'adhoc',
    created_at: '2024-09-25T15:20:00Z',
    last_activity: '2024-10-17T12:45:00Z',
    
    total_leads: 78,
    emails_sent: 78,
    emails_opened: 62,
    emails_clicked: 20,
    replies_received: 16,
    positive_replies: 10,
    meetings_booked: 5,
    deals_closed: 2,
    
    open_rate: 79.5,
    click_rate: 25.6,
    reply_rate: 20.5,
    positive_reply_rate: 12.8,
    meeting_rate: 6.4,
    close_rate: 2.6,
    
    hot_leads: 5,
    warm_leads: 10,
    cold_leads: 34,
    interested_leads: 16,
    not_interested_leads: 13,
    
    total_variants: 2,
    active_variants: 2,
    best_performing_variant: 'Interactive Design Gallery'
  },
  {
    id: '14',
    name: 'Supply Chain Optimization',
    client_id: '8923',
    client_name: 'Global Logistics Pro',
    status: 'Active',
    campaign_type: 'bulk',
    created_at: '2024-09-10T09:15:00Z',
    last_activity: '2024-10-17T08:30:00Z',
    
    total_leads: 312,
    emails_sent: 312,
    emails_opened: 234,
    emails_clicked: 70,
    replies_received: 62,
    positive_replies: 39,
    meetings_booked: 18,
    deals_closed: 7,
    
    open_rate: 75.0,
    click_rate: 22.4,
    reply_rate: 19.9,
    positive_reply_rate: 12.5,
    meeting_rate: 5.8,
    close_rate: 2.2,
    
    hot_leads: 18,
    warm_leads: 39,
    cold_leads: 137,
    interested_leads: 62,
    not_interested_leads: 56,
    
    total_variants: 3,
    active_variants: 3,
    best_performing_variant: 'Cost Savings Calculator'
  },
  {
    id: '15',
    name: 'Healthcare Compliance Solutions',
    client_id: '3456',
    client_name: 'HealthTech Innovations',
    status: 'Active',
    campaign_type: 'events',
    created_at: '2024-08-15T13:45:00Z',
    last_activity: '2024-10-17T15:45:00Z',
    
    total_leads: 198,
    emails_sent: 198,
    emails_opened: 149,
    emails_clicked: 45,
    replies_received: 40,
    positive_replies: 25,
    meetings_booked: 12,
    deals_closed: 5,
    
    open_rate: 75.3,
    click_rate: 22.7,
    reply_rate: 20.2,
    positive_reply_rate: 12.6,
    meeting_rate: 6.1,
    close_rate: 2.5,
    
    hot_leads: 12,
    warm_leads: 25,
    cold_leads: 87,
    interested_leads: 40,
    not_interested_leads: 34,
    
    total_variants: 3,
    active_variants: 2,
    best_performing_variant: 'HIPAA Compliance Checklist'
  }
]

// ============================================================================
// DEMO CAMPAIGN ANALYTICS (Last 30 days of impressive performance)
// ============================================================================

export const DEMO_CAMPAIGN_ANALYTICS: DemoCampaignAnalytics[] = [
  // Campaign 1 - Q4 Enterprise Outreach (Last 30 days)
  { campaign_id: '1', date: '2024-10-17', emails_sent: 23, emails_opened: 16, emails_clicked: 4, replies_received: 4, positive_replies: 2, meetings_booked: 1, bounces: 1, unsubscribes: 0 },
  { campaign_id: '1', date: '2024-10-16', emails_sent: 18, emails_opened: 13, emails_clicked: 3, replies_received: 3, positive_replies: 2, meetings_booked: 1, bounces: 0, unsubscribes: 0 },
  { campaign_id: '1', date: '2024-10-15', emails_sent: 25, emails_opened: 18, emails_clicked: 5, replies_received: 5, positive_replies: 3, meetings_booked: 1, bounces: 1, unsubscribes: 0 },
  { campaign_id: '1', date: '2024-10-14', emails_sent: 20, emails_opened: 14, emails_clicked: 3, replies_received: 3, positive_replies: 2, meetings_booked: 1, bounces: 0, unsubscribes: 0 },
  { campaign_id: '1', date: '2024-10-13', emails_sent: 22, emails_opened: 15, emails_clicked: 4, replies_received: 4, positive_replies: 2, meetings_booked: 1, bounces: 1, unsubscribes: 0 },
  { campaign_id: '1', date: '2024-10-12', emails_sent: 19, emails_opened: 13, emails_clicked: 3, replies_received: 3, positive_replies: 2, meetings_booked: 1, bounces: 0, unsubscribes: 0 },
  { campaign_id: '1', date: '2024-10-11', emails_sent: 24, emails_opened: 17, emails_clicked: 4, replies_received: 4, positive_replies: 3, meetings_booked: 1, bounces: 1, unsubscribes: 0 },
  { campaign_id: '1', date: '2024-10-10', emails_sent: 21, emails_opened: 15, emails_clicked: 3, replies_received: 3, positive_replies: 2, meetings_booked: 1, bounces: 0, unsubscribes: 0 },
  { campaign_id: '1', date: '2024-10-09', emails_sent: 23, emails_opened: 16, emails_clicked: 4, replies_received: 4, positive_replies: 2, meetings_booked: 1, bounces: 1, unsubscribes: 0 },
  { campaign_id: '1', date: '2024-10-08', emails_sent: 17, emails_opened: 12, emails_clicked: 3, replies_received: 3, positive_replies: 2, meetings_booked: 1, bounces: 0, unsubscribes: 0 },
  
  // Campaign 2 - Product Launch Series B (Last 30 days)
  { campaign_id: '2', date: '2024-10-17', emails_sent: 12, emails_opened: 9, emails_clicked: 2, replies_received: 2, positive_replies: 1, meetings_booked: 1, bounces: 0, unsubscribes: 0 },
  { campaign_id: '2', date: '2024-10-16', emails_sent: 15, emails_opened: 11, emails_clicked: 3, replies_received: 3, positive_replies: 2, meetings_booked: 1, bounces: 0, unsubscribes: 0 },
  { campaign_id: '2', date: '2024-10-15', emails_sent: 10, emails_opened: 8, emails_clicked: 2, replies_received: 2, positive_replies: 1, meetings_booked: 0, bounces: 0, unsubscribes: 0 },
  { campaign_id: '2', date: '2024-10-14', emails_sent: 13, emails_opened: 10, emails_clicked: 3, replies_received: 3, positive_replies: 2, meetings_booked: 1, bounces: 0, unsubscribes: 0 },
  { campaign_id: '2', date: '2024-10-13', emails_sent: 11, emails_opened: 8, emails_clicked: 2, replies_received: 2, positive_replies: 1, meetings_booked: 1, bounces: 0, unsubscribes: 0 },
  { campaign_id: '2', date: '2024-10-12', emails_sent: 14, emails_opened: 10, emails_clicked: 3, replies_received: 3, positive_replies: 2, meetings_booked: 1, bounces: 0, unsubscribes: 0 },
  { campaign_id: '2', date: '2024-10-11', emails_sent: 9, emails_opened: 7, emails_clicked: 2, replies_received: 2, positive_replies: 1, meetings_booked: 0, bounces: 0, unsubscribes: 0 },
  { campaign_id: '2', date: '2024-10-10', emails_sent: 12, emails_opened: 9, emails_clicked: 2, replies_received: 2, positive_replies: 1, meetings_booked: 1, bounces: 0, unsubscribes: 0 },
  { campaign_id: '2', date: '2024-10-09', emails_sent: 16, emails_opened: 12, emails_clicked: 3, replies_received: 3, positive_replies: 2, meetings_booked: 1, bounces: 0, unsubscribes: 0 },
  { campaign_id: '2', date: '2024-10-08', emails_sent: 8, emails_opened: 6, emails_clicked: 2, replies_received: 2, positive_replies: 1, meetings_booked: 0, bounces: 0, unsubscribes: 0 },
  
  // Campaign 3 - Partnership Development (Last 30 days)
  { campaign_id: '3', date: '2024-10-17', emails_sent: 8, emails_opened: 6, emails_clicked: 2, replies_received: 2, positive_replies: 1, meetings_booked: 1, bounces: 0, unsubscribes: 0 },
  { campaign_id: '3', date: '2024-10-16', emails_sent: 6, emails_opened: 5, emails_clicked: 1, replies_received: 1, positive_replies: 1, meetings_booked: 0, bounces: 0, unsubscribes: 0 },
  { campaign_id: '3', date: '2024-10-15', emails_sent: 9, emails_opened: 7, emails_clicked: 2, replies_received: 2, positive_replies: 1, meetings_booked: 1, bounces: 0, unsubscribes: 0 },
  { campaign_id: '3', date: '2024-10-14', emails_sent: 7, emails_opened: 6, emails_clicked: 2, replies_received: 2, positive_replies: 1, meetings_booked: 0, bounces: 0, unsubscribes: 0 },
  { campaign_id: '3', date: '2024-10-13', emails_sent: 5, emails_opened: 4, emails_clicked: 1, replies_received: 1, positive_replies: 1, meetings_booked: 0, bounces: 0, unsubscribes: 0 },
  { campaign_id: '3', date: '2024-10-12', emails_sent: 8, emails_opened: 6, emails_clicked: 2, replies_received: 2, positive_replies: 1, meetings_booked: 1, bounces: 0, unsubscribes: 0 },
  { campaign_id: '3', date: '2024-10-11', emails_sent: 6, emails_opened: 5, emails_clicked: 1, replies_received: 1, positive_replies: 1, meetings_booked: 0, bounces: 0, unsubscribes: 0 },
  { campaign_id: '3', date: '2024-10-10', emails_sent: 4, emails_opened: 3, emails_clicked: 1, replies_received: 1, positive_replies: 1, meetings_booked: 0, bounces: 0, unsubscribes: 0 },
  { campaign_id: '3', date: '2024-10-09', emails_sent: 7, emails_opened: 6, emails_clicked: 2, replies_received: 2, positive_replies: 1, meetings_booked: 1, bounces: 0, unsubscribes: 0 },
  { campaign_id: '3', date: '2024-10-08', emails_sent: 5, emails_opened: 4, emails_clicked: 1, replies_received: 1, positive_replies: 1, meetings_booked: 0, bounces: 0, unsubscribes: 0 }
]

// ============================================================================
// DEMO LEADS (Sample leads for each campaign)
// ============================================================================

export const DEMO_LEADS: DemoLead[] = [
  // Campaign 1 - Q4 Enterprise Outreach
  {
    id: '1',
    email: 'alex.johnson@techventures.io',
    first_name: 'Alex',
    last_name: 'Johnson',
    company: 'TechVentures Capital',
    title: 'Investment Director',
    industry: 'Venture Capital',
    campaign_id: '1',
    campaign_name: 'Q4 Enterprise Outreach - Fortune 500',
    client_id: '5559',
    status: 'meeting_booked',
    lead_score: 'Hot',
    created_at: '2024-10-01T09:00:00Z',
    last_activity: '2024-10-15T14:30:00Z',
    emails_sent: 3,
    reply_body: 'Hi Sarah, this sounds very interesting. I\'d love to learn more about your AI analytics platform. Can we schedule a call next week?',
    meeting_date: '2024-10-22T15:00:00Z',
    deal_value: 50000
  },
  {
    id: '2',
    email: 'emma.davis@innovatedesign.com',
    first_name: 'Emma',
    last_name: 'Davis',
    company: 'Innovate Design Studio',
    title: 'Creative Director',
    industry: 'Design',
    campaign_id: '1',
    campaign_name: 'Q4 Enterprise Outreach - Fortune 500',
    client_id: '5559',
    status: 'positive_reply',
    lead_score: 'Warm',
    created_at: '2024-10-02T10:15:00Z',
    last_activity: '2024-10-16T11:20:00Z',
    emails_sent: 2,
    reply_body: 'Thanks for reaching out! We\'re always looking for innovative analytics solutions. Would love to see a demo.',
    deal_value: 25000
  },
  {
    id: '3',
    email: 'michael.chen@summittech.io',
    first_name: 'Michael',
    last_name: 'Chen',
    company: 'Summit Tech Solutions',
    title: 'VP of Engineering',
    industry: 'Technology',
    campaign_id: '1',
    campaign_name: 'Q4 Enterprise Outreach - Fortune 500',
    client_id: '5559',
    status: 'replied',
    lead_score: 'Warm',
    created_at: '2024-10-03T14:45:00Z',
    last_activity: '2024-10-17T09:30:00Z',
    emails_sent: 1,
    reply_body: 'Interesting approach. Can you send me more details about the technical implementation?'
  },
  
  // Campaign 2 - Product Launch Series B
  {
    id: '4',
    email: 'sarah.wilson@velocitymarketing.com',
    first_name: 'Sarah',
    last_name: 'Wilson',
    company: 'Velocity Marketing Group',
    title: 'Head of Growth',
    industry: 'Marketing',
    campaign_id: '2',
    campaign_name: 'Product Launch - Series B Targets',
    client_id: '8241',
    status: 'deal_won',
    lead_score: 'Hot',
    created_at: '2024-09-15T11:30:00Z',
    last_activity: '2024-10-10T16:00:00Z',
    emails_sent: 4,
    reply_body: 'This is exactly what we need for our Series B launch! Let\'s move forward.',
    meeting_date: '2024-10-05T14:00:00Z',
    deal_value: 75000
  },
  {
    id: '5',
    email: 'david.rodriguez@acmecorp.com',
    first_name: 'David',
    last_name: 'Rodriguez',
    company: 'Acme Corporation',
    title: 'Marketing Director',
    industry: 'Manufacturing',
    campaign_id: '2',
    campaign_name: 'Product Launch - Series B Targets',
    client_id: '8241',
    status: 'meeting_booked',
    lead_score: 'Warm',
    created_at: '2024-09-16T13:20:00Z',
    last_activity: '2024-10-12T10:15:00Z',
    emails_sent: 3,
    reply_body: 'Great timing! We\'re planning our Q4 product launch. Would love to discuss.',
    meeting_date: '2024-10-25T11:00:00Z',
    deal_value: 40000
  },
  
  // Campaign 3 - Partnership Development
  {
    id: '6',
    email: 'lisa.thompson@globallogistics.com',
    first_name: 'Lisa',
    last_name: 'Thompson',
    company: 'Global Logistics Pro',
    title: 'Partnership Manager',
    industry: 'Logistics',
    campaign_id: '3',
    campaign_name: 'Partnership Development - Strategic Alliances',
    client_id: '7392',
    status: 'positive_reply',
    lead_score: 'Hot',
    created_at: '2024-08-20T14:15:00Z',
    last_activity: '2024-10-14T15:30:00Z',
    emails_sent: 2,
    reply_body: 'This partnership opportunity aligns perfectly with our growth strategy. Let\'s explore this further.',
    deal_value: 100000
  },
  {
    id: '7',
    email: 'james.kim@healthtech.io',
    first_name: 'James',
    last_name: 'Kim',
    company: 'HealthTech Innovations',
    title: 'Business Development Director',
    industry: 'Healthcare',
    campaign_id: '3',
    campaign_name: 'Partnership Development - Strategic Alliances',
    client_id: '7392',
    status: 'replied',
    lead_score: 'Warm',
    created_at: '2024-08-21T09:45:00Z',
    last_activity: '2024-10-16T12:00:00Z',
    emails_sent: 1,
    reply_body: 'Interesting proposal. Can you provide more details about the mutual benefits?'
  }
]

// ============================================================================
// DEMO DOMAINS (Company domains for each client)
// ============================================================================

export const DEMO_DOMAINS: DemoDomain[] = [
  // NorthStar Analytics domains
  {
    id: '1',
    domain: 'northstaranalytics.com',
    client_id: '5559',
    type: 'primary',
    registrar: 'GoDaddy',
    status: 'Active',
    purchase_date: '2023-01-15',
    renewal_date: '2025-01-15',
    expiry_date: '2026-01-15',
    auto_renewal: true,
    notes: 'Primary company domain'
  },
  {
    id: '2',
    domain: 'northstar-ai.com',
    client_id: '5559',
    type: 'secondary',
    registrar: 'Namecheap',
    status: 'Active',
    purchase_date: '2023-06-20',
    renewal_date: '2025-06-20',
    expiry_date: '2026-06-20',
    auto_renewal: true,
    notes: 'AI-focused subdomain'
  },
  
  // Velocity Marketing Group domains
  {
    id: '3',
    domain: 'velocitymarketing.com',
    client_id: '8241',
    type: 'primary',
    registrar: 'Cloudflare',
    status: 'Active',
    purchase_date: '2022-11-10',
    renewal_date: '2025-11-10',
    expiry_date: '2026-11-10',
    auto_renewal: true,
    notes: 'Main marketing domain'
  },
  {
    id: '3b',
    domain: 'velocity-growth.com',
    client_id: '8241',
    type: 'secondary',
    registrar: 'Cloudflare',
    status: 'Active',
    purchase_date: '2024-03-10',
    renewal_date: '2025-03-10',
    expiry_date: '2026-03-10',
    auto_renewal: true,
    notes: 'Secondary growth domain'
  },
  
  // Summit Tech Solutions domains
  {
    id: '4',
    domain: 'summittech.io',
    client_id: '7392',
    type: 'primary',
    registrar: 'Google Domains',
    status: 'Active',
    purchase_date: '2022-03-05',
    renewal_date: '2025-03-05',
    expiry_date: '2026-03-05',
    auto_renewal: true,
    notes: 'Enterprise software domain'
  },
  {
    id: '5',
    domain: 'summit-solutions.com',
    client_id: '7392',
    type: 'secondary',
    registrar: 'GoDaddy',
    status: 'Active',
    purchase_date: '2023-09-12',
    renewal_date: '2025-09-12',
    expiry_date: '2026-09-12',
    auto_renewal: false,
    notes: 'Legacy domain'
  },
  
  // Innovate Design Studio domains
  {
    id: '6',
    domain: 'innovatedesign.com',
    client_id: '4567',
    type: 'primary',
    registrar: 'Squarespace',
    status: 'Active',
    purchase_date: '2023-04-18',
    renewal_date: '2025-04-18',
    expiry_date: '2026-04-18',
    auto_renewal: true,
    notes: 'Creative agency domain'
  },
  {
    id: '6b',
    domain: 'innovate-studio.io',
    client_id: '4567',
    type: 'secondary',
    registrar: 'Namecheap',
    status: 'Active',
    purchase_date: '2024-03-12',
    renewal_date: '2025-03-12',
    expiry_date: '2026-03-12',
    auto_renewal: true,
    notes: 'Secondary creative brand domain'
  },
  
  // Acme Corporation domains
  {
    id: '7',
    domain: 'acmecorp.com',
    client_id: '2891',
    type: 'primary',
    registrar: 'Network Solutions',
    status: 'Active',
    purchase_date: '2021-08-30',
    renewal_date: '2025-08-30',
    expiry_date: '2026-08-30',
    auto_renewal: true,
    notes: 'Corporate domain'
  },
  {
    id: '7b',
    domain: 'acme-corp.io',
    client_id: '2891',
    type: 'secondary',
    registrar: 'Cloudflare',
    status: 'Active',
    purchase_date: '2024-02-01',
    renewal_date: '2025-02-01',
    expiry_date: '2026-02-01',
    auto_renewal: true,
    notes: 'Secondary domain for testing and outbound'
  },
  
  // TechVentures Capital domains
  {
    id: '8',
    domain: 'techventures.io',
    client_id: '6734',
    type: 'primary',
    registrar: 'Namecheap',
    status: 'Active',
    purchase_date: '2022-12-01',
    renewal_date: '2025-12-01',
    expiry_date: '2026-12-01',
    auto_renewal: true,
    notes: 'Venture capital domain'
  },
  {
    id: '8b',
    domain: 'techventures-capital.com',
    client_id: '6734',
    type: 'secondary',
    registrar: 'GoDaddy',
    status: 'Active',
    purchase_date: '2024-01-20',
    renewal_date: '2025-01-20',
    expiry_date: '2026-01-20',
    auto_renewal: true,
    notes: 'Secondary corporate domain'
  },
  
  // Global Logistics Pro domains (fixed typo)
  {
    id: '9',
    domain: 'globallogistics.com',
    client_id: '8923',
    type: 'primary',
    registrar: 'GoDaddy',
    status: 'Active',
    purchase_date: '2022-05-22',
    renewal_date: '2025-05-22',
    expiry_date: '2026-05-22',
    auto_renewal: true,
    notes: 'Logistics company domain'
  },
  {
    id: '9b',
    domain: 'global-logistics.io',
    client_id: '8923',
    type: 'secondary',
    registrar: 'Cloudflare',
    status: 'Active',
    purchase_date: '2024-04-05',
    renewal_date: '2025-04-05',
    expiry_date: '2026-04-05',
    auto_renewal: true,
    notes: 'Secondary outbound domain'
  },
  
  // HealthTech Innovations domains
  {
    id: '10',
    domain: 'healthtechinnovations.com',
    client_id: '3456',
    type: 'primary',
    registrar: 'Cloudflare',
    status: 'Active',
    purchase_date: '2023-02-14',
    renewal_date: '2025-02-14',
    expiry_date: '2026-02-14',
    auto_renewal: true,
    notes: 'Healthcare technology domain'
  },
  {
    id: '10b',
    domain: 'healthtech-platform.io',
    client_id: '3456',
    type: 'secondary',
    registrar: 'Namecheap',
    status: 'Active',
    purchase_date: '2024-02-28',
    renewal_date: '2025-02-28',
    expiry_date: '2026-02-28',
    auto_renewal: true,
    notes: 'Secondary product/platform domain'
  }
]

// ==========================================================================
// DOMAIN → TARGET MAILBOX PLAN (deterministic counts sum to EXACTLY 156)
// ==========================================================================
// Base totals per client were specified (126). We add +30 across clients to
// reach 156, proportionally favoring primary domains.

export const DEMO_DOMAIN_PLAN: Record<string, { clientId: string; type: 'primary'|'secondary'; target: number } > = {
  // NorthStar Analytics (16 → 20)
  'northstaranalytics.com': { clientId: '5559', type: 'primary', target: 12 },
  'northstar-ai.com':       { clientId: '5559', type: 'secondary', target: 8 },

  // Velocity Marketing Group (12 → 15), add a secondary velocity-growth.com
  'velocitymarketing.com':  { clientId: '8241', type: 'primary', target: 11 },
  'velocity-growth.com':    { clientId: '8241', type: 'secondary', target: 4 },

  // Summit Tech Solutions (20 → 24)
  'summittech.io':          { clientId: '7392', type: 'primary', target: 14 },
  'summit-solutions.com':   { clientId: '7392', type: 'secondary', target: 10 },

  // Innovate Design Studio (12 → 15)
  'innovatedesign.com':     { clientId: '4567', type: 'primary', target: 11 },
  'innovate-studio.io':     { clientId: '4567', type: 'secondary', target: 4 },

  // Acme Corporation (20 → 25)
  'acmecorp.com':           { clientId: '2891', type: 'primary', target: 15 },
  'acme-corp.io':           { clientId: '2891', type: 'secondary', target: 10 },

  // TechVentures Capital (14 → 17)
  'techventures.io':        { clientId: '6734', type: 'primary', target: 11 },
  'techventures-capital.com': { clientId: '6734', type: 'secondary', target: 6 },

  // Global Logistics Pro (16 → 20)
  'globallogistics.com':    { clientId: '8923', type: 'primary', target: 12 },
  'global-logistics.io':    { clientId: '8923', type: 'secondary', target: 8 },

  // HealthTech Innovations (16 → 20)
  'healthtechinnovations.com': { clientId: '3456', type: 'primary', target: 12 },
  'healthtech-platform.io':    { clientId: '3456', type: 'secondary', target: 8 }
}

// Sanity check for total 156
export const DEMO_TOTAL_MAILBOXES: number = Object.values(DEMO_DOMAIN_PLAN).reduce((s, v) => s + v.target, 0)

// ============================================================================
// DEMO MAILBOXES (Professional email accounts)
// ============================================================================

// Regenerated deterministically from DEMO_DOMAIN_PLAN to exactly 156 mailboxes
export const DEMO_MAILBOXES: DemoMailbox[] = (() => {
  const names = [
    ['Sarah','Chen'], ['Marcus','Rodriguez'], ['Emily','Thompson'], ['David','Kim'],
    ['Jessica','Martinez'], ['Alex','Johnson'], ['Michael','Brown'], ['Lisa','Wilson'],
    ['John','Davis'], ['Emma','Garcia'], ['Chris','Miller'], ['Amy','Jones'],
    ['Tom','Taylor'], ['Olivia','Anderson'], ['Daniel','Thomas'], ['Sophia','Moore'],
    ['James','Martin'], ['Grace','Lee'], ['Ethan','Clark'], ['Ava','Hall']
  ]

  const roleAliasesPrimary = ['sales','outreach','growth','marketing','partnerships','success','support','operations']
  const roleAliasesSecondary = ['team','hello','contact','info','start','connect','pipeline','bd']

  let id = 1
  const result: DemoMailbox[] = []

  const pushMailbox = (first: string, last: string, domain: string, clientId: string, type: 'primary'|'secondary', i: number) => {
    const provider: 'GMAIL' | 'OUTLOOK' = Math.random() > 0.5 ? 'GMAIL' : 'OUTLOOK'
    const primaryPatterns = [
      `${first.toLowerCase()}@${domain}`,
      `${first.toLowerCase()}.${last.toLowerCase()}@${domain}`,
      `${first[0].toLowerCase()}.${last.toLowerCase()}@${domain}`
    ]
    const secondaryPatterns = [
      `${first.toLowerCase()}@${domain}`,
      `${(i % 2 === 0 ? 'team' : 'hello')}@${domain}`,
      `contact@${domain}`
    ]
    const role = (type === 'primary' ? roleAliasesPrimary[i % roleAliasesPrimary.length] : roleAliasesSecondary[i % roleAliasesSecondary.length])
    const email = (i % 4 === 3) ? `${role}@${domain}` : (type === 'primary' ? primaryPatterns[i % primaryPatterns.length] : secondaryPatterns[i % secondaryPatterns.length])

    const warmupReputation = type === 'primary' ? (85 + (i % 11)) : (75 + (i % 11))
    const dailyLimit = type === 'primary' ? 50 + (i % 31) : 30 + (i % 21)
    const emailsTodayBand = (i % 4)
    const emailsToday = emailsTodayBand === 0 ? 5 + (i % 6) : emailsTodayBand === 1 ? 10 + (i % 6) : emailsTodayBand === 2 ? 15 + (i % 6) : 18 + (i % 4)

    result.push({
      id: String(id++),
      email,
      domain,
      from_name: `${first} ${last}`,
      client_id: clientId,
      status: 'Active',
      warmup_status: type === 'primary' ? 'ACTIVE' : 'ACTIVE',
      warmup_reputation: warmupReputation,
      daily_limit: dailyLimit,
      emails_sent_today: emailsToday,
      reply_rate: 15 + (i % 8),
      bounce_rate: 1 + (i % 3),
      created_at: '2024-07-01T10:00:00Z',
      last_activity: '2024-10-17T12:00:00Z',
      provider,
      type
    })
  }

  const domains = Object.keys(DEMO_DOMAIN_PLAN)
  domains.forEach((domain) => {
    const plan = DEMO_DOMAIN_PLAN[domain]
    for (let i = 0; i < plan.target; i++) {
      const [first, last] = names[(i + domain.length) % names.length]
      pushMailbox(first, last, domain, plan.clientId, plan.type, i)
    }
  })

  // safety check
  return result.slice(0, 156)
})()

// ============================================================================
// DEMO VARIANTS (A/B test results with clear winners)
// ============================================================================

export const DEMO_VARIANTS: DemoVariant[] = [
  // Campaign 1 - Q4 Enterprise Outreach
  {
    id: '1',
    campaign_id: '1',
    name: 'Personalized AI Insights',
    subject_line: 'How {{company}} can increase revenue by 30% with AI',
    email_template: 'AI_INSIGHTS_TEMPLATE',
    status: 'Active',
    emails_sent: 156,
    open_rate: 72.4,
    click_rate: 19.2,
    reply_rate: 19.2,
    positive_reply_rate: 12.8,
    is_winner: true,
    created_at: '2024-10-01T09:00:00Z'
  },
  {
    id: '2',
    campaign_id: '1',
    name: 'Industry Benchmark Report',
    subject_line: '{{industry}} benchmark report: Where you stand',
    email_template: 'BENCHMARK_TEMPLATE',
    status: 'Active',
    emails_sent: 162,
    open_rate: 68.5,
    click_rate: 17.9,
    reply_rate: 17.9,
    positive_reply_rate: 10.5,
    is_winner: false,
    created_at: '2024-10-01T09:00:00Z'
  },
  {
    id: '3',
    campaign_id: '1',
    name: 'ROI Calculator',
    subject_line: 'Calculate your potential ROI in 2 minutes',
    email_template: 'ROI_CALCULATOR_TEMPLATE',
    status: 'Active',
    emails_sent: 169,
    open_rate: 69.2,
    click_rate: 18.3,
    reply_rate: 18.3,
    positive_reply_rate: 11.2,
    is_winner: false,
    created_at: '2024-10-01T09:00:00Z'
  },
  
  // Campaign 2 - Product Launch Series B
  {
    id: '4',
    campaign_id: '2',
    name: 'Exclusive Early Access',
    subject_line: 'Exclusive: Early access to our Series B launch',
    email_template: 'EARLY_ACCESS_TEMPLATE',
    status: 'Active',
    emails_sent: 78,
    open_rate: 76.9,
    click_rate: 23.1,
    reply_rate: 21.8,
    positive_reply_rate: 12.8,
    is_winner: true,
    created_at: '2024-09-15T11:30:00Z'
  },
  {
    id: '5',
    campaign_id: '2',
    name: 'Founder Story',
    subject_line: 'The story behind our Series B funding',
    email_template: 'FOUNDER_STORY_TEMPLATE',
    status: 'Active',
    emails_sent: 78,
    open_rate: 73.1,
    click_rate: 21.8,
    reply_rate: 19.2,
    positive_reply_rate: 11.5,
    is_winner: false,
    created_at: '2024-09-15T11:30:00Z'
  },
  {
    id: '6',
    campaign_id: '2',
    name: 'Product Demo',
    subject_line: 'See our new features in action',
    email_template: 'PRODUCT_DEMO_TEMPLATE',
    status: 'Active',
    emails_sent: 78,
    open_rate: 74.4,
    click_rate: 22.4,
    reply_rate: 20.5,
    positive_reply_rate: 12.2,
    is_winner: false,
    created_at: '2024-09-15T11:30:00Z'
  },
  
  // Campaign 3 - Partnership Development
  {
    id: '7',
    campaign_id: '3',
    name: 'Mutual Growth Opportunity',
    subject_line: 'Partnership opportunity: 40% revenue growth potential',
    email_template: 'PARTNERSHIP_GROWTH_TEMPLATE',
    status: 'Active',
    emails_sent: 78,
    open_rate: 80.8,
    click_rate: 25.6,
    reply_rate: 22.4,
    positive_reply_rate: 14.1,
    is_winner: true,
    created_at: '2024-08-20T14:15:00Z'
  },
  {
    id: '8',
    campaign_id: '3',
    name: 'Strategic Alliance',
    subject_line: 'Strategic alliance opportunity with {{company}}',
    email_template: 'STRATEGIC_ALLIANCE_TEMPLATE',
    status: 'Active',
    emails_sent: 78,
    open_rate: 78.2,
    click_rate: 23.1,
    reply_rate: 20.5,
    positive_reply_rate: 12.8,
    is_winner: false,
    created_at: '2024-08-20T14:15:00Z'
  }
]

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export function getDemoClientById(clientId: string): DemoClient | undefined {
  return DEMO_CLIENTS.find(client => client.id === clientId)
}

export function getDemoCampaignsByClientId(clientId: string): DemoCampaign[] {
  return DEMO_CAMPAIGNS.filter(campaign => campaign.client_id === clientId)
}

export function getDemoCampaignById(campaignId: string): DemoCampaign | undefined {
  return DEMO_CAMPAIGNS.find(campaign => campaign.id === campaignId)
}

export function getDemoLeadsByCampaignId(campaignId: string): DemoLead[] {
  return DEMO_LEADS.filter(lead => lead.campaign_id === campaignId)
}

export function getDemoAnalyticsByCampaignId(campaignId: string): DemoCampaignAnalytics[] {
  return DEMO_CAMPAIGN_ANALYTICS.filter(analytics => analytics.campaign_id === campaignId)
}

export function getDemoVariantsByCampaignId(campaignId: string): DemoVariant[] {
  return DEMO_VARIANTS.filter(variant => variant.campaign_id === campaignId)
}

export function getDemoMailboxesByClientId(clientId: string): DemoMailbox[] {
  return DEMO_MAILBOXES.filter(mailbox => mailbox.client_id === clientId)
}

// New utilities for domains/mailboxes health and volume
export function getDemoMailboxesByDomain(domain: string): DemoMailbox[] {
  return DEMO_MAILBOXES.filter(m => m.domain === domain)
}

export function getDemoDomainsByClientId(clientId: string) {
  return DEMO_DOMAINS.filter(d => d.client_id === clientId)
}

export function getMailboxCountByDomain(): Record<string, number> {
  return DEMO_MAILBOXES.reduce((acc, m) => {
    acc[m.domain] = (acc[m.domain] || 0) + 1
    return acc
  }, {} as Record<string, number>)
}

export function getDemoMailboxHealth(): { healthy: number, warning: number, error: number } {
  let healthy = 0, warning = 0, error = 0
  DEMO_MAILBOXES.forEach(m => {
    const rep = m.warmup_reputation
    if (rep >= 85) healthy++
    else if (rep >= 70) warning++
    else error++
  })
  return { healthy, warning, error }
}

export function getDemoSendingVolumeByDomain(domain: string, days: number) {
  // Approximate daily sending using emails_sent_today as base
  const mbs = getDemoMailboxesByDomain(domain)
  const today = mbs.reduce((s, m) => s + (m.emails_sent_today || 0), 0)
  // simple backfill decay model
  const series: number[] = []
  for (let i = 0; i < days; i++) {
    series.push(Math.round(today * (1 - (i * 0.03))))
  }
  return series
}

// Calculate aggregate metrics for demo data
export function getDemoAggregateMetrics() {
  const totalCampaigns = DEMO_CAMPAIGNS.length
  const activeCampaigns = DEMO_CAMPAIGNS.filter(c => c.status === 'Active').length
  const totalLeads = DEMO_CAMPAIGNS.reduce((sum, c) => sum + c.total_leads, 0)
  const totalEmailsSent = DEMO_CAMPAIGNS.reduce((sum, c) => sum + c.emails_sent, 0)
  const totalReplies = DEMO_CAMPAIGNS.reduce((sum, c) => sum + c.replies_received, 0)
  const totalPositiveReplies = DEMO_CAMPAIGNS.reduce((sum, c) => sum + c.positive_replies, 0)
  const totalMeetings = DEMO_CAMPAIGNS.reduce((sum, c) => sum + c.meetings_booked, 0)
  const totalDeals = DEMO_CAMPAIGNS.reduce((sum, c) => sum + c.deals_closed, 0)
  
  const avgReplyRate = totalEmailsSent > 0 ? (totalReplies / totalEmailsSent) * 100 : 0
  const avgPositiveReplyRate = totalEmailsSent > 0 ? (totalPositiveReplies / totalEmailsSent) * 100 : 0
  const avgMeetingRate = totalEmailsSent > 0 ? (totalMeetings / totalEmailsSent) * 100 : 0
  const avgCloseRate = totalEmailsSent > 0 ? (totalDeals / totalEmailsSent) * 100 : 0
  
  return {
    totalCampaigns,
    activeCampaigns,
    totalLeads,
    totalEmailsSent,
    totalReplies,
    totalPositiveReplies,
    totalMeetings,
    totalDeals,
    avgReplyRate: Math.round(avgReplyRate * 10) / 10,
    avgPositiveReplyRate: Math.round(avgPositiveReplyRate * 10) / 10,
    avgMeetingRate: Math.round(avgMeetingRate * 10) / 10,
    avgCloseRate: Math.round(avgCloseRate * 10) / 10
  }
}
